/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { csrfPeerRecovery } from 'app_honda_cars/coordinator/csrfPeerRecovery';
import { checkoutOwnership } from 'app_honda_cars/checkout/checkoutOwnership';
import { dwService } from 'amb/services/dw.service';
import { sharedStorage } from 'app_honda_cars/utils/sharedStorage';
import { locationStorage } from 'app_honda_cars/utils/locationStorage';
import { tabIdentity } from 'app_honda_cars/utils/tabIdentity';
import { urlHelper } from 'amb/helpers/url.helper';
import { feedback } from 'app_honda_cars/utils/feedback';

/**
 * @typedef {import('app_honda_cars/utils/feedback').ModalOptions} ModalOptions
 */

/**
 * JSON error / conflict payload from VTI / VTR / VOW (and CSRF) AJAX responses.
 * Labels come from server `attachMessages` (`tabIsolation` bundle).
 *
 * @typedef {Object} TabIsolationResponse
 *
 * @property {string} [action] SFRA pipeline action (e.g. `CheckoutServices-PlaceOrder`)
 * @property {boolean} [error]
 * @property {string} [code] wire code (`SESSION_EXPIRED`, `CHECKOUT_TAB_CHANGED`, `DEALER_NOT_FOUND`, …)
 * @property {string} [reason]
 * @property {boolean} [canAcquire]
 * @property {string} [checkoutTab]
 * @property {string} [tabId]
 * @property {string} [title]
 * @property {string} [message]
 * @property {string} [primaryBtn]
 * @property {string} [secondaryBtn] omitted / empty → hide Cancel
 * @property {string} [redirectUrl]
 * @property {boolean} [csrfError] SFRA CSRF middleware flag (no `code`)
 */

/**
 * Global jQuery / HTMX bus for tab-isolation request wiring and conflict recovery.
 *
 * **Scope:**
 * - Prefilter: `X-SF-CC-Tab-Isolation` + optional `mcTab` + optional `X-SF-CC-Location`
 * - Lazy Client Tab Id mint on configurator / checkout entry ({@link ajaxHandlers.initTabId})
 * - Error bus: `ajaxError` + `htmx:responseError` + custom `ajax:error`
 *   → {@link ajaxHandlers.helpers.handleError}
 * - CSRF peer bus: publish / subscribe `csrf-stale`
 * - `SESSION_EXPIRED` → modal then Reload (Cancel same)
 * - `CHECKOUT_IN_USE` / `CHECKOUT_TAB_CHANGED` → modal; Confirm reclaim when
 *   `CHECKOUT_IN_USE` or `canAcquire`, else Reload; dismiss no-op
 * - `TAB_ISOLATION_MISMATCH` / `INVALID_CONFIGURATION` / `TAB_ISOLATION_DISABLED`
 *   → modal; Confirm → Reload (or strip query for invalid config); dismiss no-op;
 *   unknown codes ignored
 * - `DEALER_NOT_FOUND` / `DEALER_MISMATCH` → modal; Confirm → configurator
 *   (`a.configurator-url` on Checkout-Begin); dismiss no-op
 */
export const ajaxHandlers = {
  /**
   * Sole Client Tab Id mint gate for tab isolation (processInclude entry, before
   * `document.ready`). Reads `.page[data-action]` and calls
   * {@link tabIdentity.getTabId} only on `Product-Show` and `Checkout-Begin`.
   *
   * Other storefront pages stay cold (no `mcTab` in storage): AJAX prefilters
   * use {@link tabIdentity.appendTabId} → {@link tabIdentity.peekTabId}, and
   * coordinator / checkout helpers must not mint via `getTabId()`.
   *
   * After mint, `sessionStorage` + `window.name` carry the id for the rest of
   * the session (including navigations away from configurator / checkout).
   */
  initTabId() {
    if (!tabIdentity.isTabIsolation()) {
      return;
    }

    const TAB_REQUIRED_PAGES = new Set(['Product-Show', 'Checkout-Begin']);
    const page = document.querySelector('.page[data-action]')?.dataset.action;

    if (TAB_REQUIRED_PAGES.has(page)) {
      tabIdentity.getTabId();
    }
  },

  /**
   * Registers jQuery/HTMX hooks (same-origin only):
   * - send client tab-isolation mode via {@link ajaxHandlers.helpers.TAB_ISOLATION_HEADER} (`1` / `0`)
   * - when isolation is on, append `mcTab` ({@link tabIdentity})
   * - when isolation is on, attach Client Tab Location via {@link ajaxHandlers.helpers.LOCATION_HEADER}
   * Cross-origin requests are left untouched (no custom headers / query mutation).
   */
  registerAjaxPrefilter() {
    const isTabIsolation = tabIdentity.isTabIsolation();
    const tabIsolationValue = isTabIsolation ? '1' : '0';
    const tabIsolationHeader = this.helpers.TAB_ISOLATION_HEADER;
    const locationHeader = this.helpers.LOCATION_HEADER;

    // jQuery
    $.ajaxPrefilter((options, originalOptions, jqXHR) => {
      if (!this.helpers.isSameOriginUrl(options.url)) {
        return;
      }

      jqXHR.setRequestHeader(tabIsolationHeader, tabIsolationValue);

      if (isTabIsolation && options.url) {
        const locationValue = locationStorage.getRequestHeaderValue();

        options.url = tabIdentity.appendTabId(options.url);

        if (locationValue) {
          jqXHR.setRequestHeader(locationHeader, locationValue);
        }
      }
    });

    // HTMX
    document.addEventListener('htmx:configRequest', (event) => {
      if (!this.helpers.isSameOriginUrl(event.detail.path)) {
        return;
      }

      event.detail.headers[tabIsolationHeader] = tabIsolationValue;

      if (isTabIsolation) {
        const locationValue = locationStorage.getRequestHeaderValue();

        event.detail.path = tabIdentity.appendTabId(event.detail.path);

        if (locationValue) {
          event.detail.headers[locationHeader] = locationValue;
        }
      }
    });
  },

  /**
   * Handle jQuery AJAX + HTMX HTTP errors (CSRF / tab-isolation / session / ownership).
   */
  registerAjaxError() {
    // jQuery
    $(document).ajaxError((event, jqXHR) => {
      this.helpers.handleError(jqXHR.responseJSON || {});
    });

    // Event Based
    $(document).on('ajax:error', (event, response = {}) => {
      this.helpers.handleError(response);
    });

    // HTMX
    document.addEventListener('htmx:responseError', (event) => {
      let response;

      try {
        response = JSON.parse(event.detail?.xhr?.responseText || 'null');
      } catch (err) {}

      this.helpers.handleError(response || {});
    });
  },

  /**
   * After Sign In / Create Account success, peers may hold stale CSRF tokens.
   * publish() lazy-starts the bus so peer tabs receive `csrf-stale` even when
   * multi-cart tab isolation is off.
   */
  registerCsrfStalePublishers() {
    $(document).on('login:success login:register:success', () => {
      csrfPeerRecovery.notifyCsrfStale();
    });
  },

  /**
   * Peer tabs: apply Site Preference recovery when another tab publishes `csrf-stale`.
   * Idempotent.
   */
  registerCsrfStaleListener() {
    if (csrfPeerRecovery.isInitialized()) {
      return;
    }

    csrfPeerRecovery.setInitialized();

    sharedStorage.tabChannel.subscribe('csrf-stale', payload => {
      csrfPeerRecovery.onCsrfStale(payload);
    });
  },

  helpers: {
    /**
     * Tab-isolation mode token (`1` / `0`). Request: client ClientData snapshot;
     * response: live server pref (VTI). Must match SFCC `X-SF-CC-*` custom header rules.
     *
     * @private
     * @readonly
     *
     * @type {string}
     */
    TAB_ISOLATION_HEADER: 'X-SF-CC-Tab-Isolation',

    /**
     * Request-scoped Client Tab Location (compact Tab Location wire, URI-encoded).
     * Server resolve prefers this over Tab Record / `userloc` for the request.
     * Must match SFCC `X-SF-CC-*` custom header rules.
     *
     * @private
     * @readonly
     *
     * @type {string}
     */
    LOCATION_HEADER: 'X-SF-CC-Location',

    /**
     * In-flight latch of wire `code`s so a burst of conflict XHRs shares one recovery.
     * Clear as soon as the shopper decides (confirm / dismiss) — before reclaim AJAX —
     * or in `.finally` / after CSRF / mismatch paths that do not reclaim. Unknown codes
     * never latch (see {@link ajaxHandlers.helpers.handleError}).
     *
     * @private
     *
     * @type {Set<string>}
     */
    handlingErrors: new Set(),

    /**
     * True when `url` resolves to the current page origin (relative URLs count as same-origin).
     *
     * @private
     *
     * @param {string} url
     *
     * @returns {boolean}
     */
    isSameOriginUrl(url) {
      try {
        return new URL(url || '', window.location.origin).origin === window.location.origin;
      } catch (e) {
        return false;
      }
    },

    /**
     * Classify known conflict JSON and delegate. Unknown / empty `code` → no-op
     * (do not steal unrelated AJAX failures into the conflict modal).
     *
     * @private
     *
     * @param {TabIsolationResponse} response
     */
    handleError(response) {
      const { code = 'unknown', csrfError } = response;

      if (this.handlingErrors.has(code)) {
        return;
      }

      if (csrfError) {
        this.handlingErrors.add('CSRF_TOKEN_MISMATCH');

        return this.handleCsrfTokenMismatch(response);
      }

      this.handlingErrors.add(code);

      switch (code) {
        case 'SESSION_EXPIRED':
          return this.handleSessionExpired(response);
        case 'CHECKOUT_IN_USE':
        case 'CHECKOUT_TAB_CHANGED':
          return this.handleCheckoutConflict(response);
        case 'CSRF_TOKEN_MISMATCH':
          return this.handleCsrfTokenMismatch(response);
        case 'DEALER_NOT_FOUND':
        case 'DEALER_MISMATCH':
        case 'TAB_ISOLATION_MISMATCH':
        case 'INVALID_CONFIGURATION':
        case 'TAB_ISOLATION_DISABLED':
          return this.handleGenericError(response);
        default:
          this.handlingErrors.delete(code);
      }
    },

    /**
     * VTR / VOW `SESSION_EXPIRED` — modal then Reload (Confirm and Cancel).
     *
     * @private
     *
     * @param {TabIsolationResponse} response
     */
    handleSessionExpired(response) {
      this
        .showConfirmModal(response)
        .then(() => this.reloadOrRedirect(response))
        .finally(() => this.handlingErrors.delete(response.code))
      ;
    },

    /**
     * VOW `CHECKOUT_IN_USE` / `CHECKOUT_TAB_CHANGED`. Confirm: reclaim when
     * `CHECKOUT_IN_USE` or `canAcquire` (same as legacy `shouldReclaim`); else
     * Reload. Dismiss → no-op.
     *
     * @private
     *
     * @param {TabIsolationResponse} response
     */
    handleCheckoutConflict(response) {
      this
        .showConfirmModal(response)
        .then((confirmed) => {
          this.handlingErrors.delete(response.code);

          if (!confirmed) {
            return;
          }

          if (response.code === 'CHECKOUT_IN_USE' || response.canAcquire) {
            return checkoutOwnership.acquireCheckout({
              checkoutTab: response.checkoutTab,
              forceReplace: true
            });
          }

          this.reloadOrRedirect(response);
        })
      ;
    },

    /**
     * @private
     *
     * @param {TabIsolationResponse} response
     */
    handleCsrfTokenMismatch(response) {
      try {
        csrfPeerRecovery.notifyCsrfStale();
        csrfPeerRecovery.handleCsrfFailSelf();
      } finally {
        this.handlingErrors.delete('CSRF_TOKEN_MISMATCH');
      }
    },

    /**
     * `TAB_ISOLATION_MISMATCH` / `INVALID_CONFIGURATION` / `TAB_ISOLATION_DISABLED`
     * — modal from response labels; Confirm →
     * {@link ajaxHandlers.helpers.reloadOrRedirect} (strips `acc`/`accpk`/`mem` when invalid config);
     * dismiss → no-op. Empty `secondaryBtn` → alert-style (no Cancel).
     *
     * @private
     *
     * @param {TabIsolationResponse} response
     */
    handleGenericError(response) {
      const { code } = response;

      this
        .showConfirmModal(response)
        .then((confirmed) => confirmed && this.reloadOrRedirect(response))
        .finally(() => this.handlingErrors.delete(code))
      ;
    },

    /**
     * Conflict confirm via {@link feedback.modal} (server labels).
     * No `secondaryBtn` → alert-style (no cancel/close, static backdrop, no ESC).
     *
     * @private
     *
     * @param {TabIsolationResponse} response
     * @param {ModalOptions} [options={}]
     *
     * @returns {Promise<boolean>} `true` if Confirm, `false` if dismissed
     */
    showConfirmModal(response, options = {}) {
      return feedback.modal({
        title: response.title,
        body: response.message,
        confirmText: response.primaryBtn,
        cancelText: response.secondaryBtn,
        centered: true,
        dismissOthers: true,
        ...options,
      });
    },

    /**
     * Navigate after conflict Confirm / mismatch dismiss when no better target.
     *
     * @private
     *
     * @param {TabIsolationResponse} [response]
     *
     * @returns {void}
     */
    reloadOrRedirect(response) {
      const page = document.querySelector('.page[data-action]')?.dataset.action;

      let url;

      switch (page) {
        case 'Checkout-Begin':
          url = document.querySelector('a.configurator-url, a.submit-purchaseType')?.href || dwService.url('cars');
          break;
        case 'Product-Show':
          url = urlHelper.prepareUrl(location.href, { exclude: ['showSummary'] });
          break;
        default:
          url = location.href;
          break;
      }

      if (response?.code === 'INVALID_CONFIGURATION') {
        url = urlHelper.prepareUrl(url, { exclude: ['acc', 'accpk', 'mem'] });
      }

      return location.replace(url);
    }
  }
};
